<script setup lang="ts">
	import AutoComplete from "~/components/inputs/AutoComplete.vue";
	import Fuse from "fuse.js";
	import emailProviders from "~/data/email_providers.json";

	defineComponent({
		name: "EmailAutocomplete",
	});

	const { searchFunction, uppercase } = defineProps({
		name: { type: String, required: false },
		label: { type: String, default: "", required: true },
		searchFunction: { type: Function as PropType<(arg: any) => void>, required: true },
		disabled: { type: Boolean, default: false },
		error: { type: Boolean, default: false },
		errorMessage: { type: String, default: "" },
		clearIncomplete: { type: Boolean, default: false, required: false },
		uppercase: { type: Boolean, default: true },
		testId: { type: String, default: "" },
		type: { type: String, default: "text" },
	});

	const value = defineModel({
		type: String,
		default: "",
	});

	const inputValue = ref<string>("");
	const domainSuggestion = ref<string>("");
	const showDomainSuggestion = ref<boolean>(false);

	const domainSearcher = new Fuse(emailProviders, {
		keys: ["domain", "errors"],
		threshold: 0.0,
		location: 0,
		distance: 0,
		minMatchCharLength: 0,
		isCaseSensitive: false,
	});

	const emit = defineEmits(["input"]);

	watch(
		value,
		(newValue) => {
			if (inputValue.value !== newValue) {
				inputValue.value = newValue;
				emit("input", newValue);
			}
		},
		{ immediate: true }
	);

	watch(inputValue, (newValue) => {
		value.value = newValue;
		emit("input", newValue);
		resetDomainSuggestion();
	});

	watch(inputValue, () => {
		checkDomainSuggestion();
	});

	const checkDomainSuggestion = () => {
		if (!inputValue.value.includes("@")) {
			resetDomainSuggestion();
			return;
		}

		const [name, domain] = inputValue.value.replaceAll(" ", "").split("@");

		const [domainName, domainExt] = (() => {
      const lastDotIndex = domain.lastIndexOf(".");
      return lastDotIndex !== -1
         ? [domain.slice(0, lastDotIndex), domain.slice(lastDotIndex + 1)]
         : [domain, ""];
   })();

		const normalizedDomain = domainName.includes(".") ? domainName.split(".")[0] : domainName;

		const results = domainSearcher.search(normalizedDomain);

		if (results.length > 0) {
			const bestMatch = results[0].item;

			if (
				bestMatch &&
				(normalizedDomain !== bestMatch.domain ||
					domainExt !== bestMatch.extension ||
					normalizedDomain !== domainName ||
					inputValue.value.includes(' '))
			) {
				domainSuggestion.value = `${name}@${bestMatch.domain}.${bestMatch.extension}`;
				showDomainSuggestion.value = true;
			}
		}
	};

	const acceptDomainSuggestion = () => {
		inputValue.value = domainSuggestion.value;
		value.value = domainSuggestion.value;
		emit("input", domainSuggestion.value);
		resetDomainSuggestion();
	};

	const resetDomainSuggestion = () => {
		domainSuggestion.value = "";
		showDomainSuggestion.value = false;
	};
</script>

<template>
	<div class="email-autocomplete">
		<AutoComplete
			:label="label"
			:searchFunction="searchFunction"
			:uppercase="uppercase"
			:disabled="disabled"
			:error="error"
			:errorMessage="errorMessage"
			v-model="inputValue"
			@input="$emit('input', $event)"
		/>
		<div v-if="showDomainSuggestion" class="suggestion">
			<small>
				Volevi dire
				<span
					class="underline cursor-pointer font-bold"
					@click="acceptDomainSuggestion"
				>
					{{ domainSuggestion }}
				</span>
				?
			</small>
		</div>
	</div>
</template>

<style scoped>
	.suggestion {
		@apply mt-2 text-sm text-gray-600;
	}
</style>
